import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import EmptyState from "@/pages/EmptyState";

/**
 * Client-side access gate. Two forms, both supported.
 *
 * PREFERRED — permission-driven:
 *   <RoleGate permission="customers.edit">…</RoleGate>
 * Resolves via useAuth().can(...), which short-circuits `isAdmin` /
 * `isSuperAdmin` per the canonical access model.
 *
 * LEGACY — role-slug allow-list (transitional):
 *   <RoleGate allow={["super_admin","admin","leasing_sales"]}>…</RoleGate>
 * Any allow list is auto-passed for Admin/Super Admin so that the
 * "Admin means every normal module" rule holds even for pages that
 * were written before the permission API existed. The role list is
 * still consulted for non-admin users.
 *
 * Backend remains authoritative (returns 403 on API); this component
 * only prevents rendering the wrong page.
 */
export const RoleGate = ({ allow, permission, children }) => {
  const { user, isAdmin, can } = useAuth();
  if (!user) return null;

  // Permission-driven path (preferred).
  if (permission) {
    if (can(permission)) return children;
    return (
      <EmptyState
        title="Insufficient permissions"
        note={`This page requires the ${permission} permission.`}
      />
    );
  }

  // Legacy allow-list path. Admin/Super Admin always pass so the
  // 13-module access model works even when a page's `allow` list
  // predates the platform-access refactor.
  if (isAdmin) return children;

  if (allow && allow.includes(user.role)) return children;

  return (
    <EmptyState
      title="Insufficient permissions"
      note={`Your role (${user.role}) does not have access to this module.`}
    />
  );
};
